import { SUBSCRIPTION_PLANS } from "../config/subscriptionPlans";

export function getPlanDefinition(planKey) {
  return SUBSCRIPTION_PLANS?.[planKey] || SUBSCRIPTION_PLANS?.free || null;
}

export function isTeamSubscriptionActive(subscription) {
  if (!subscription) return false;

  const normalizedStatus =
    typeof subscription?.status === "string" ? subscription.status.trim().toLowerCase() : "";

  return (
    normalizedStatus === "active" ||
    normalizedStatus === "trialing" ||
    normalizedStatus === "paid" ||
    subscription?.isActive === true
  );
}

function buildBillingHoldFeatures() {
  return {
    vodReviewer: false,
    teamStats: false,
    teamCalendar: false,
    prepCenter: false,
    personalCalendar: false,
    personalAvailability: false,
    personalHighlights: false,
    personalFavorites: false,
    notes: false,
    lfg: true,
  };
}

function buildLegacyResolvedTeamEntitlements(subscription) {
  const active = isTeamSubscriptionActive(subscription);

  const hasRealPlan =
    typeof subscription?.planKey === "string" && subscription.planKey.length > 0;

  const resolvedPlanKey =
    active && hasRealPlan ? subscription.planKey : "free";

  const plan = getPlanDefinition(resolvedPlanKey) || {};

  const maxMembersOverride =
    typeof subscription?.maxMembersOverride === "number"
      ? subscription.maxMembersOverride
      : null;

  const storageGbOverride =
    typeof subscription?.storageGbOverride === "number"
      ? subscription.storageGbOverride
      : null;

  const normalizedStatus =
    typeof subscription?.status === "string" ? subscription.status.trim().toLowerCase() : "";

  const baseFeatures =
    active && hasRealPlan
      ? { ...(plan.features || {}) }
      : normalizedStatus === "renewal_blocked" ||
          normalizedStatus === "billing_hold" ||
          subscription?.setupStatus === "billing_hold"
        ? buildBillingHoldFeatures()
      : {
          vodReviewer: false,
          teamStats: false,
          teamCalendar: false,
          prepCenter: false,
          personalCalendar: false,
          personalAvailability: false,
          personalHighlights: false,
          personalFavorites: false,
          notes: false,
          lfg: false,
        };

  return {
    planKey: resolvedPlanKey,
    isActive: active && hasRealPlan,
    billingInterval: subscription?.billingInterval || null,
    billingCurrency: subscription?.billingCurrency || null,
    storageGb: active && hasRealPlan ? (storageGbOverride ?? plan.storageGb ?? 0) : 0,
    maxMembers: active && hasRealPlan ? (maxMembersOverride ?? plan.maxMembers ?? 0) : 0,
    pricingMode: subscription?.pricingMode || "per_user",
    fullTeamModeActive: subscription?.fullTeamModeActive === true,
    billableSeatCount:
      typeof subscription?.billableSeatCount === "number"
        ? subscription.billableSeatCount
        : null,
    cappedAtFiveUsers: subscription?.cappedAtFiveUsers === true,
    resolvedRole: subscription?.resolvedRole || null,
    restrictedByRole: subscription?.restrictedByRole === true,
    features: {
      ...baseFeatures,
      ...(active && hasRealPlan ? (subscription?.featureOverrides || {}) : {}),
    },
  };
}

export function resolveTeamEntitlements(subscriptionOrEntitlements) {
  if (!subscriptionOrEntitlements) return null;

  // Preferred path:
  // backend already returned resolved entitlements
  if (
    subscriptionOrEntitlements.features &&
    typeof subscriptionOrEntitlements.features === "object" &&
    (
      "planKey" in subscriptionOrEntitlements ||
      "maxMembers" in subscriptionOrEntitlements ||
      "restrictedByRole" in subscriptionOrEntitlements ||
      "pricingMode" in subscriptionOrEntitlements
    )
  ) {
    return subscriptionOrEntitlements;
  }

  // Compatibility path:
  // wrapper object carrying entitlements
  if (
    subscriptionOrEntitlements.entitlements &&
    typeof subscriptionOrEntitlements.entitlements === "object"
  ) {
    return subscriptionOrEntitlements.entitlements;
  }

  // Legacy fallback:
  // raw subscription object from older frontend flows
  return buildLegacyResolvedTeamEntitlements(subscriptionOrEntitlements);
}

export function resolveUserPersonalEntitlements(teamSubscriptions = []) {
  const hasAnyPaidTeam = teamSubscriptions.some(
    (sub) => isTeamSubscriptionActive(sub) && sub.planKey !== "free"
  );

  return {
    hasAnyPaidTeam,
    features: {
      personalCalendar: hasAnyPaidTeam,
      personalAvailability: hasAnyPaidTeam,
      personalHighlights: hasAnyPaidTeam,
      personalFavorites: hasAnyPaidTeam,
    },
  };
}

export function hasTeamFeature(teamEntitlements, featureKey) {
  return Boolean(teamEntitlements?.features?.[featureKey]);
}

export function hasPersonalFeature(userPersonalEntitlements, featureKey) {
  return Boolean(userPersonalEntitlements?.features?.[featureKey]);
}
